import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import Modal from '../../../components/common/Modal';
import { TranscriptEditorStore } from '../../TranscriptEditorStore';
import { ReplaceTextGloballyCommand } from '../../commands/ReplaceTextGloballyCommand';
import { ReplaceSelectedTextCommand } from '../../commands/ReplaceSelectedTextCommand';

interface GlobalReplaceModalProps {
  isOpen: boolean;
  onClose: () => void;
  originalText: string;
  editorStore: TranscriptEditorStore;
  onReplace: (originalText: string, newText: string) => Promise<void>;
}

/**
 * Modal component for globally replacing text across the entire transcript
 */
const GlobalReplaceModal: React.FC<GlobalReplaceModalProps> = ({
  isOpen,
  onClose,
  originalText,
  editorStore,
  onReplace,
}) => {
  const [newText, setNewText] = useState('');
  const [isReplacing, setIsReplacing] = useState(false);
  const [occurrenceCount, setOccurrenceCount] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [replaceGlobally, setReplaceGlobally] = useState(false);

  // Reset state when modal opens with new original text
  useEffect(() => {
    if (isOpen && originalText) {
      setNewText('');
      setIsReplacing(false);
      setError(null);
      setReplaceGlobally(false);
      
      // Count occurrences of the text in the transcript
      if (editorStore.container) {
        let count = 0;
        editorStore.container.segments.forEach(segment => {
          segment.paragraphs.forEach(paragraph => {
            paragraph.elements.forEach(element => {
              if (element.type === 'text' && element.value) {
                // Count occurrences in each element
                const regex = new RegExp(escapeRegExp(originalText), 'gi');
                const matches = element.value.match(regex);
                if (matches) {
                  count += matches.length;
                }
              }
            });
          });
        });
        setOccurrenceCount(count);
      }
    }
  }, [isOpen, originalText, editorStore]);

  // Escape special characters in the search string for regex
  const escapeRegExp = (string: string) => {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  };

  // Handle the replace action
  const handleReplace = async () => {
    if (!newText) {
      setError('New text cannot be empty');
      return;
    }

    setIsReplacing(true);
    setError(null);

    try {
      if (replaceGlobally) {
        // If replacing globally, use the ReplaceTextGloballyCommand
        const command = new ReplaceTextGloballyCommand(editorStore.editor, originalText, newText, '');
        // add it to the command manager
        await editorStore.editor.commandManager.execute(command);
      } else {
        // If replacing in selection only, handle via selection service
        const selectionService = editorStore.editor.getSelectionService();
        if (!selectionService || !selectionService.hasSelection) {
          setError('No text selected for replacement');
          return;
        }

        // Get the selected elements
        const selectedElements = selectionService.getSelectedElements();
        if (selectedElements.length === 0) {
          setError('No text selected for replacement');
          return;
        }

        
        // Filter to only include text elements
        const textElements = selectedElements.filter(el => el.type === 'text');
        
        if (textElements.length === 0) {
          setError('No text elements selected for replacement');
          return;
        }
        
        // If we have selected elements, use our new command
        const command = new ReplaceSelectedTextCommand(
          editorStore.editor,
          textElements,
          newText,
          ''
        );
        
        // Execute the command
        await editorStore.editor.commandManager.execute(command);
      }
      
      onClose();
    } catch (err) {
      setError('Failed to replace text. Please try again.');
      console.error('Error replacing text:', err);
    } finally {
      setIsReplacing(false);
    }
  };

  // Prevent selection changes when clicking in the modal
  const preventSelectionChange = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    return false;
  };

  return (
    <Modal isOpen={isOpen} closeModal={onClose} backgroundColor="rgba(0, 0, 0, 0.7)">
      <ModalContent
        onMouseDown={preventSelectionChange}
        onMouseUp={preventSelectionChange}
        onMouseEnter={preventSelectionChange}
        onMouseLeave={preventSelectionChange}
        onMouseMove={preventSelectionChange}
        data-selection-menu="true"
      >
        <ModalHeader
          onMouseDown={preventSelectionChange}
          onMouseUp={preventSelectionChange}
        >{replaceGlobally ? 'Replace Text Globally' : 'Replace Selected Text'}</ModalHeader>
        <ModalBody
          onMouseDown={preventSelectionChange}
          onMouseUp={preventSelectionChange}
        >
          <InfoText>
            {replaceGlobally ? (
              <>Found <strong>{occurrenceCount}</strong> occurrences of the selected text.</>
            ) : (
              <>Replace text in <strong>selected text</strong> only.</>
            )}
            {replaceGlobally && occurrenceCount > 0 && (
              <WarningText>This will replace all occurrences throughout the entire transcript.</WarningText>
            )}
          </InfoText>

          <FormGroup onMouseDown={preventSelectionChange} onMouseUp={preventSelectionChange}>
            <Label onMouseDown={preventSelectionChange} onMouseUp={preventSelectionChange}>Original Text:</Label>
            <ReadOnlyInput 
              value={originalText} 
              disabled 
              onMouseDown={e => e.stopPropagation()}
              onMouseUp={e => e.stopPropagation()}
            />
          </FormGroup>

          <FormGroup onMouseDown={preventSelectionChange} onMouseUp={preventSelectionChange}>
            <Label onMouseDown={preventSelectionChange} onMouseUp={preventSelectionChange}>Replace With:</Label>
            <Input
              value={newText}
              onChange={(e) => setNewText(e.target.value)}
              placeholder="Enter replacement text"
              autoFocus
              onMouseDown={e => e.stopPropagation()}
              onMouseUp={e => e.stopPropagation()}
            />
          </FormGroup>
          
          <CheckboxGroup onMouseDown={preventSelectionChange} onMouseUp={preventSelectionChange}>
            <Checkbox 
              type="checkbox" 
              id="replaceGlobally" 
              checked={replaceGlobally}
              onChange={(e) => setReplaceGlobally(e.target.checked)}
              onMouseDown={e => e.stopPropagation()}
              onMouseUp={e => e.stopPropagation()}
            />
            <CheckboxLabel 
              htmlFor="replaceGlobally"
              onMouseDown={preventSelectionChange}
              onMouseUp={preventSelectionChange}
            >
              Replace Globally
              {replaceGlobally && occurrenceCount > 1 && (
                <span> (will replace {occurrenceCount} occurrences)</span>
              )}
            </CheckboxLabel>
          </CheckboxGroup>

          {error && <ErrorMessage onMouseDown={preventSelectionChange} onMouseUp={preventSelectionChange}>{error}</ErrorMessage>}
        </ModalBody>

        <ModalFooter onMouseDown={preventSelectionChange} onMouseUp={preventSelectionChange}>
          <CancelButton 
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onClose();
            }} 
            onMouseDown={preventSelectionChange}
            onMouseUp={preventSelectionChange}
            disabled={isReplacing}
          >
            Cancel
          </CancelButton>
          <ReplaceButton 
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleReplace();
            }} 
            onMouseDown={preventSelectionChange}
            onMouseUp={preventSelectionChange}
            disabled={isReplacing || (replaceGlobally && occurrenceCount === 0)}
            $isActive={!replaceGlobally || occurrenceCount > 0}
          >
            {isReplacing ? 'Replacing...' : replaceGlobally ? `Replace All (${occurrenceCount})` : 'Replace'}
          </ReplaceButton>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

// Styled components
const ModalContent = styled.div`
  background-color: #1e1e2c;
  border-radius: 8px;
  width: 500px;
  max-width: 90vw;
  color: #f3e9d7;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);
`;

const ModalHeader = styled.div`
  padding: 16px 20px;
  background-color: #2a2a3c;
  font-size: 18px;
  font-weight: bold;
  border-bottom: 1px solid #333;
`;

const ModalBody = styled.div`
  padding: 20px;
`;

const ModalFooter = styled.div`
  padding: 16px 20px;
  background-color: #2a2a3c;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  border-top: 1px solid #333;
`;

const FormGroup = styled.div`
  margin-bottom: 16px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 6px;
  font-size: 14px;
  color: #aaaab9;
`;

const Input = styled.input`
  width: 95%;
  padding: 10px;
  background-color: #2a2a3c;
  border: 1px solid #444;
  border-radius: 4px;
  color: #f3e9d7;
  font-size: 14px;
  
  &:focus {
    border-color: #4c9aff;
    outline: none;
  }
`;

const ReadOnlyInput = styled.input`
  width: 95%;
  padding: 10px;
  background-color: #252535;
  border: 1px solid #444;
  border-radius: 4px;
  color: #aaaab9;
  font-size: 14px;
  cursor: not-allowed;
`;

const CancelButton = styled.button`
  padding: 8px 16px;
  background-color: transparent;
  border: 1px solid #444;
  border-radius: 4px;
  color: #aaaab9;
  cursor: pointer;
  font-size: 14px;
  
  &:hover:not(:disabled) {
    background-color: rgba(255, 255, 255, 0.05);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ReplaceButton = styled.button<{ $isActive: boolean }>`
  padding: 8px 16px;
  background-color: ${props => props.$isActive ? '#4c9aff' : '#555'};
  border: none;
  border-radius: 4px;
  color: white;
  cursor: ${props => props.$isActive ? 'pointer' : 'not-allowed'};
  font-size: 14px;
  font-weight: 500;
  
  &:hover:not(:disabled) {
    background-color: ${props => props.$isActive ? '#3a85e0' : '#555'};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const InfoText = styled.div`
  margin-bottom: 20px;
  line-height: 1.5;
`;

const WarningText = styled.div`
  color: #ffcc66;
  margin-top: 8px;
  font-size: 14px;
`;

const ErrorMessage = styled.div`
  color: #ff6b6b;
  margin-top: 12px;
  padding: 8px 12px;
  background-color: rgba(255, 107, 107, 0.1);
  border-radius: 4px;
  font-size: 14px;
`;

const CheckboxGroup = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 16px;
`;

const Checkbox = styled.input`
  margin-right: 8px;
  cursor: pointer;
  width: 16px;
  height: 16px;
`;

const CheckboxLabel = styled.label`
  font-size: 14px;
  color: #f3e9d7;
  cursor: pointer;
  
  span {
    font-size: 13px;
    color: #aaaab9;
    margin-left: 4px;
  }
`;

export default GlobalReplaceModal;